import { rem } from "@certn/cascadia";
import {
  lineHeights,
  sizes,
  textStyles,
  weights,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import React, { useEffect } from "react";
import { AriaComboBoxOptions, useComboBox, useFilter } from "react-aria";
import { useTranslation } from "react-i18next";
import { useComboBoxState } from "react-stately";

import { FieldErrorsProps, Text, useColors } from "@certn/cascadia-react";

import { ListBox } from "./ListBox";
import { Popover } from "./Popover";
import { FormFeedback } from "../../components/inputs/_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../../components/inputs/_assets/input-wrapper/FormLabel";
import { FormText } from "../../components/inputs/_assets/input-wrapper/FormText";
import { InputWrapper } from "../../components/inputs/_assets/input-wrapper/InputWrapper";
import { Icon } from "../../foundations/icons/Icon/Icon";

export interface SearchableSelectProps<T>
  extends Partial<AriaComboBoxOptions<T>> {
  label: string;
  children: React.ReactElement[] | React.ReactElement;
  successMessage?: string;
  info?: React.ReactNode;
  width?: string;
  helper?: string;
  testID?: string;
  allowsCustomValue?: boolean;
  hasHighlightOptions?: boolean;
  isGoogleAutocomplete?: boolean;
  closeText?: string;
  defaultSelectedKey?: string;
  trailingItem?: JSX.Element;
  onChange?: (value: string) => void;
  errors?: FieldErrorsProps["children"];
}

/**
 * @deprecated use SearchableSelectRAC instead - based on React Aria Components
 */
export const SearchableSelect = <T extends object>(
  props: SearchableSelectProps<T>
) => {
  const colors = useColors();
  const error = Boolean(props.errors);
  const { t } = useTranslation();
  const { label, validationState, testID = "searchable-select" } = props;
  const { contains } = useFilter({
    sensitivity: "base",
    ignorePunctuation: true,
  });
  const state = useComboBoxState({
    ...props,
    defaultFilter: contains,
  });
  const { isDisabled, isReadOnly, placeholder } = props;

  const values = textStyles["input/text-field"];
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listBoxRef = React.useRef<HTMLUListElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { inputProps, listBoxProps, errorMessageProps, labelProps } =
    useComboBox(
      {
        ...props,
        inputRef,
        listBoxRef,
        popoverRef,
        buttonRef,
      },
      state
    );

  const minPopoverWidth = inputRef?.current?.clientWidth;

  useEffect(() => {
    // during async debounce the items array is empty momentarily
    // we need to force the listbox to open
    if (props.items) state.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.items]);

  // handleChange override
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onChange) props.onChange(e?.target?.value);
    if (inputProps.onChange) inputProps.onChange(e);
  };

  const handleButtonClick = () => {
    state.toggle();
    if (inputRef.current) inputRef?.current?.focus();
  };

  const handleMouseUp = () => {
    state.toggle();
  };

  return (
    <InputWrapper>
      <FormLabel
        {...labelProps}
        isRequired={props.isRequired}
        info={props.info}
      >
        {label}
      </FormLabel>
      <div
        className={css({
          position: "relative",
          flexGrow: 1,
        })}
      >
        <input
          {...inputProps}
          // accessible menu open on mouse up with pointer cancellation
          // https://www.w3.org/WAI/WCAG21/Understanding/pointer-cancellation.html
          onMouseUp={handleMouseUp}
          placeholder={validationState === "invalid" ? "" : placeholder}
          className={css(
            styles.input,
            isDisabled && {
              color: "var(--text-disabled)",
              border: `1px solid ${colors["border-input-disabled"]}`,
            },
            {
              background: "var(--background-input)",
              "&:invalid": {
                border: `1px solid ${colors["border-danger-input"]}`,
              },
              "::placeholder": {
                color: "var(--text-placeholder)",
              },
            },
            !isDisabled && {
              border: "none",
              color: "var(--text-base)",
            },
            isReadOnly && {
              background: "var(--background-input-disabled)",
              color: "var(--text-weak)",
            },
            {
              boxShadow: error
                ? `inset 0 0 0 1px ${colors["border-danger-input"]}`
                : isDisabled
                  ? `inset 0 0 0 1px ${colors["background-input-disabled"]}`
                  : `inset 0 0 0 1px ${colors["border-strong"]}`,
              fontSize: rem(sizes[values.size]),
              lineHeight: rem(lineHeights[values.lineHeight]),
              fontWeight: weights[values.weight],
              backgroundColor:
                isDisabled || isReadOnly
                  ? "var(--background-input-disabled)"
                  : "var(--background-input)",
              "&:hover:not(:focus)": isDisabled
                ? { borderColor: undefined }
                : !error
                  ? {
                      borderColor: "var(--border-input-active)",
                    }
                  : null,
              "&:focus": error
                ? css([
                    {
                      outline: "0",
                      borderColor: "var(--border-danger-input)",
                      background: "var(--background-base)",
                      boxShadow: "var(--focus-input-error)",
                    },
                  ])
                : css([
                    {
                      outline: "0",
                      borderColor: "var(--border-input-active)",
                      boxShadow: "var(--focus-input)",
                    },
                  ]),
            }
          )}
          ref={inputRef}
          data-testid={testID + "-input"}
          onChange={handleChange}
        />
        {!props.isGoogleAutocomplete && (
          <div
            className={css({
              position: "absolute",
              top: 0,
              bottom: 0,
              right: 0,
              paddingRight: "var(--space-3)",
              display: "flex",
              alignItems: "center",
            })}
          >
            <button
              className={css({
                color: "var(--icon-base)",
                background: "none",
                border: "none",
                padding: 0,
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                },
              })}
              type="button"
              ref={buttonRef}
              data-testid={testID}
              onClick={handleButtonClick}
              aria-label={state.isOpen ? t("Search") : t("Expand menu")}
              tabIndex={-1}
              disabled={props.isDisabled}
            >
              <Icon
                name={state.isOpen ? "search" : "expand_more"}
                color="icon-base"
                type="standard"
                size="space-5"
              />
            </button>
          </div>
        )}
      </div>
      {state.isOpen && (
        <Popover
          popoverRef={popoverRef}
          triggerRef={inputRef}
          state={state}
          isNonModal
          placement="bottom start"
          minPopoverWidth={(minPopoverWidth as number) + 2}
        >
          <ListBox
            {...listBoxProps}
            listBoxRef={listBoxRef}
            state={state}
            hasHighlightOptions={props.hasHighlightOptions}
            trailingItem={props.trailingItem}
            width={props.width ?? (minPopoverWidth as number) + 2 + "px"}
            closePanel={
              props.closeText
                ? closePanel(props.closeText, state.close)
                : undefined
            }
          />
        </Popover>
      )}
      {props.helper && <FormText>{props.helper}</FormText>}
      <FormFeedback {...errorMessageProps}>{props.errors}</FormFeedback>
      <FormFeedback type="valid">{props.successMessage}</FormFeedback>
    </InputWrapper>
  );
};

const closePanel = (closeText?: string, close?: () => void) => (
  <div
    className={css({
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "var(--space-3)",
      textTransform: "uppercase",
    })}
  >
    <Text color="text-weak">{closeText}</Text>
    <div
      aria-hidden="true"
      onClick={close}
      className={css({ cursor: "pointer" })}
    >
      <Icon name="close" color="icon-base" />
    </div>
  </div>
);

const styles = {
  input: css({
    WebkitAppearance: "none",
    width: "100%",
    boxSizing: "border-box",
    paddingTop: "var(--space-2)",
    paddingRight: "var(--space-3)",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    borderRadius: "var(--border-radius-2)",
    fontFamily: "var(--font-family-default)",
  }),
};
